import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faTimes, 
  faSpinner, 
  faMapMarkerAlt,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { doc, setDoc } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { liteClient } from 'algoliasearch/lite';
import styles from './LocationSearchModal.module.css';

// Initialize Algolia client
const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

// Cloud Functions endpoints
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://ontour.co.in/api' 
  : 'http://localhost:5001/ontourapp/us-central1';

const LocationSearchModal = ({ isOpen, onClose, onSelect, title = 'Search Location' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [sessionToken, setSessionToken] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isGoogleSearching, setIsGoogleSearching] = useState(false);
  const [isSearchButtonPressed, setIsSearchButtonPressed] = useState(false);
  const [searchMode, setSearchMode] = useState('algolia');
  const [googleSuggestions, setGoogleSuggestions] = useState([]);

  // Parse address function
  const parseLocationFromAddress = (formattedAddress) => {
    if (!formattedAddress) return { city: '', state: '', country: '', pincode: '' };
    
    // Split the address by commas
    const parts = formattedAddress.split(',').map(part => part.trim());
    
    // Need at least 3 parts for this to work (city, state+pincode, country)
    if (parts.length < 3) return { city: '', state: '', country: '', pincode: '' };
    
    // Get the country from the last part
    const country = parts[parts.length - 1];
    
    // Get the state and pincode from the second-to-last part (e.g., "Maharashtra 400059")
    const stateAndPincode = parts[parts.length - 2];
    
    // Try to extract the pincode using a regex pattern
    // Looking for a sequence of digits (typically 5-6 digits for a pincode)
    const pincodeMatch = stateAndPincode.match(/\b(\d{5,6})\b/);
    
    let state = stateAndPincode;
    let pincode = '';
    
    if (pincodeMatch) {
      pincode = pincodeMatch[0].trim();
      // Remove the pincode from the state part
      state = stateAndPincode.replace(pincodeMatch[0], '').trim();
    }
    
    // Get the city from the third-to-last part
    const city = parts[parts.length - 3].trim();
    
    return { city, state, country, pincode };
  };

  useEffect(() => {
    generateSessionToken();
    
    // Reset search states when component unmounts
    return () => {
      setIsSearching(false);
      setIsGoogleSearching(false);
    };
  }, []);

  // Reset states when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setIsSearching(false);
      setIsGoogleSearching(false);
      setIsSearchButtonPressed(false);
    }
  }, [isOpen]);

  const generateSessionToken = () => {
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    setSessionToken(uuid);
  };

  const handleAlgoliaSearch = async () => {
    if (!searchQuery.trim() || isSearching || isGoogleSearching) return;
    
    try {
      setIsSearching(true);
      setSearchMode('algolia');
      
      const { results } = await client.search([{
        indexName: 'places_index',
        query: searchQuery,
        hitsPerPage: 5
      }]);
  
      if (results[0].hits.length === 0) {
        setSuggestions([]);
        setSearchMode('noResults');
      } else {
        setSuggestions(results[0].hits);
      }
      
      setIsSearchButtonPressed(true);
    } catch (error) {
      console.error('Error searching places with Algolia:', error);
      setSuggestions([]);
      setSearchMode('noResults');
      setIsSearchButtonPressed(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleGoogleSearch = async () => {
    if (!searchQuery.trim() || isGoogleSearching) return;
    
    try {
      setIsGoogleSearching(true);
      setSearchMode('google');
      
      const response = await fetch(`${API_BASE_URL}/searchPlaces`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchQuery,
          sessionToken
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.suggestions && data.suggestions.length > 0) {
        setGoogleSuggestions(data.suggestions);
      } else {
        setGoogleSuggestions([]);
      }
      
      setIsSearchButtonPressed(true);
    } catch (error) {
      console.error('Error searching with Google Places API:', error);
      setGoogleSuggestions([]);
    } finally {
      setIsGoogleSearching(false);
    }
  };

  const handleSearch = () => {
    if (isSearching || isGoogleSearching) return;
    handleAlgoliaSearch();
  };

  const handleSelectAlgoliaPlace = (place) => {
    // Extract location data from address if not available
    const { city, state, country, pincode } = place.city && place.state && place.country 
      ? { 
          city: place.city || '', 
          state: place.state || '', 
          country: place.country || '', 
          pincode: place.pincode || '' 
        }
      : parseLocationFromAddress(place.formattedAddress);

    setSelectedPlace({
      id: place.objectID || '',
      name: place.name || '',
      displayName: place.displayName || '',
      formattedAddress: place.formattedAddress || '',
      shortFormattedAddress: place.shortFormattedAddress || '',
      location: place.location || null,
      placeCoverPhotoUrl: place.placeCoverPhotoUrl || '',
      googleMapsUri: place.googleMapsUri || '',
      // Add location fields
      country,
      state,
      city,
      pincode,
    });
    setSuggestions([]);
    setGoogleSuggestions([]);
    setIsSearchButtonPressed(false);
  };

  const handleSelectGooglePlace = async (placeId) => {
    try {
      setIsSearching(true);
      
      const response = await fetch(`${API_BASE_URL}/getPlaceDetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          placeId
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const placeData = await response.json();
      
      let placeCoverPhotoUrl = '';
      if (placeData.photos && placeData.photos.length > 0) {
        const photoName = placeData.photos[0].name;
        const photoResponse = await fetch(`${API_BASE_URL}/getPlacePhoto`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            photoName
          }),
        });
        
        if (photoResponse.ok) {
          const photoData = await photoResponse.json();
          placeCoverPhotoUrl = photoData.photoUri || '';
        }
      }
      
      // Extract country, state, city from address components
      let country = '';
      let state = '';
      let city = '';
      let pincode = '';

      // First try using address components if available
      if (placeData.addressComponents && placeData.addressComponents.length > 0) {
        // Parse address components to extract country, state, city
        placeData.addressComponents.forEach(component => {
          const types = component.types || [];
          
          if (types.includes('country')) {
            country = component.longText?.text || component.shortText?.text || '';
          } else if (types.includes('administrative_area_level_1')) {
            state = component.longText?.text || component.shortText?.text || '';
          } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
            city = component.longText?.text || component.shortText?.text || '';
          } else if (types.includes('postal_code')) {
            pincode = component.longText?.text || component.shortText?.text || '';
          }
        });
      }
      
      // If address components didn't work, parse from formatted address
      if (!country || !state || !city) {
        const locationData = parseLocationFromAddress(placeData.formattedAddress || '');
        if (!country) country = locationData.country;
        if (!state) state = locationData.state;
        if (!city) city = locationData.city;
        if (!pincode) pincode = locationData.pincode;
      }
      
      const placeDetails = {
        id: placeData.id || '',
        name: placeData.name || '',
        displayName: placeData.displayName?.text || '',
        formattedAddress: placeData.formattedAddress || '',
        shortFormattedAddress: placeData.shortFormattedAddress || '',
        location: placeData.location || null,
        placeCoverPhotoUrl,
        googleMapsUri: placeData.googleMapsUri || '',
        // Add the new fields
        country,
        state,
        city,
        pincode,
      };

      setSelectedPlace(placeDetails);
      setSuggestions([]);
      setGoogleSuggestions([]);
      setIsSearchButtonPressed(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
      alert('Failed to fetch place details. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirm = async () => {
    if (selectedPlace) {
      try {
        const db = getFirestoreInstance();
        const placeRef = doc(db, 'Places', selectedPlace.id);
        await setDoc(placeRef, selectedPlace);
        
        onSelect(selectedPlace);
        onClose();
      } catch (error) {
        console.error('Error saving place:', error);
        alert('Failed to save place. Please try again.');
      }
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && !isSearching && !isGoogleSearching) {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setGoogleSuggestions([]);
    setIsSearchButtonPressed(false);
    setSearchMode('algolia');
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{title}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.searchContainer}>
          <div className={styles.searchInputWrapper}>
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for a location"
              className={styles.searchInput}
              onKeyDown={handleSearchKeyDown}
            />
            {searchQuery && (
              <button 
                onClick={handleClearSearch}
                className={styles.clearButton}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <button 
            onClick={handleSearch}
            disabled={!searchQuery.trim() || isSearching || isGoogleSearching}
            className={styles.searchButton}
          >
            {(isSearching || isGoogleSearching) ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSearch} />
            )}
          </button>
        </div>

        {/* Search Results */}
        {isSearchButtonPressed && searchMode === 'algolia' && suggestions.length > 0 && (
          <div className={styles.suggestionsContainer}>
            {suggestions.map((place) => (
              <button
                key={place.objectID}
                className={styles.suggestionItem}
                onClick={() => handleSelectAlgoliaPlace(place)}
              >
                <div className={styles.suggestionContent}>
                  <div className={styles.suggestionName}>{place.displayName}</div>
                  <div className={styles.suggestionAddress}>{place.formattedAddress}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {isSearchButtonPressed && searchMode === 'google' && googleSuggestions.length > 0 && (
          <div className={styles.suggestionsContainer}>
            {googleSuggestions.map((suggestion) => (
              <button
                key={suggestion.placePrediction.placeId}
                className={styles.suggestionItem}
                onClick={() => handleSelectGooglePlace(suggestion.placePrediction.placeId)}
              >
                <div className={styles.suggestionContent}>
                  <div className={styles.suggestionName}>
                    {suggestion.placePrediction.text.text}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {isSearchButtonPressed && searchMode === 'noResults' && !isSearching && !isGoogleSearching && (
          <div className={styles.noResultsContainer}>
            <p className={styles.noResultsText}>No results found in our database</p>
            <button 
              onClick={handleGoogleSearch}
              className={styles.googleSearchButton}
              disabled={isGoogleSearching}
            >
              {isGoogleSearching ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              )}
              Search from Maps
            </button>
          </div>
        )}

        {selectedPlace && (
          <div className={styles.selectedLocation}>
            <h3 className={styles.selectedLocationHeader}>Selected Location</h3>
            <div className={styles.locationCard}>
              {selectedPlace.placeCoverPhotoUrl && (
                <img
                  src={selectedPlace.placeCoverPhotoUrl}
                  alt={selectedPlace.displayName}
                  className={styles.locationImage}
                />
              )}
              <div className={styles.locationDetails}>
                <p className={styles.locationName}>{selectedPlace.displayName}</p>
                <p className={styles.locationAddress}>{selectedPlace.formattedAddress}</p>
                <p className={styles.locationRegion}>
                  {[
                    selectedPlace.city, 
                    selectedPlace.state, 
                    selectedPlace.pincode, 
                    selectedPlace.country
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.checkIcon} />
            </div>

            <div className={styles.mapContainer}>
              <iframe
                title={`Map view of ${selectedPlace.displayName}`}
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_MAPS_EMBED_KEY}&q=${encodeURIComponent(selectedPlace.formattedAddress)}&zoom=15`}
                allowFullScreen
              />
            </div>
          </div>
        )}

        <div className={styles.buttonContainer}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button 
            className={styles.selectButton} 
            onClick={handleConfirm} 
            disabled={!selectedPlace}
          >
            Select Location
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationSearchModal;